import { Component } from 'react';
import { Result, Button } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import type { ErrorBoundaryState, ErrorBoundaryProps } from '../types/props';
import { ERROR_TEXT } from '../constants/text';

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console for debugging
    console.error('Error caught by boundary:', error, errorInfo);

    // React 19: Enhanced error reporting with more context
    if (import.meta.env.MODE === 'development') {
      console.group('React 19 Error Boundary Details');
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
      console.error('Error Boundary Location:', this.constructor.name);
      console.groupEnd();
    }

    // In a production app, you would send this to an error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleReload() {
    window.location.reload();
  }

  handleGoHome() {
    window.location.href = '/';
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="sa-p-6 sa-text-align-center">
          <Result
            status="error"
            title={ERROR_TEXT.GENERIC_TITLE}
            subTitle={ERROR_TEXT.GENERIC_SUBTITLE}
            extra={[
              <Button
                key="reload"
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
              >
                {ERROR_TEXT.RELOAD}
              </Button>,
              <Button key="home" icon={<HomeOutlined />} onClick={this.handleGoHome}>
                {ERROR_TEXT.GO_HOME}
              </Button>,
            ]}
          />
          {import.meta.env.MODE === 'development' && this.state.error && (
            <details className="sa-mt-5 sa-text-align-left">
              <summary>Error Details (Development Only)</summary>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '10px',
                  overflow: 'auto',
                }}
              >
                {this.state.error && this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
